// useWcBoard — reads get_wc2026_board RPC for the WC 2026 Hub. Honest read:
// fixtures + h2h reference odds only, no fabricated standings or bracket.
//
// The RPC returns:
//   { generated_at, kickoff_date, total_fixtures, fixtures_with_odds,
//     days: [ { date, fixtures: [ { fixture_id, home_team, away_team,
//                                   kickoff_at, status, odds: {home,draw,away}|null }
//                                ] } ] }

import { useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../../supabase/browser.ts";

export interface WcOdds { home: number; draw: number; away: number }
export interface WcFixture {
  fixture_id: string;
  home_team: string;
  away_team: string;
  kickoff_at: string;
  status: string;
  odds: WcOdds | null;
}
export interface WcDay { date: string; fixtures: WcFixture[] }
export interface WcTeamFixtureRef {
  fixture_id: string; opponent: string; side: "H" | "A"; kickoff_at: string;
}
export interface WcTeam {
  team: string; fixtures: number; first_kickoff: string;
  fixture_list: WcTeamFixtureRef[];
}
export interface WcFuture { outright: string; odds: number; code: string }
export type WcBracketRound = "R32" | "R16" | "QF" | "SF" | "3rd" | "F";
export interface WcBracketFixture {
  fixture_id: string; home_team: string; away_team: string;
  kickoff_at: string; odds: WcOdds | null;
}
export interface WcSignal {
  fixture_id: string; home_team: string; away_team: string;
  kickoff_at: string; market: string; selection: string;
  fair_odds: number | null; market_odds: number | null;
  clv_gap: number | null; conviction_tier: string | null;
  desk_confidence: number | null;
}

export interface WcBoard {
  generated_at: string;
  kickoff_date: string;
  total_fixtures: number;
  fixtures_with_odds: number;
  days: WcDay[];
  teams?: WcTeam[];
  futures?: WcFuture[];
  bracket?: Partial<Record<WcBracketRound, WcBracketFixture[]>>;
  signals?: WcSignal[];
}

interface UseWcBoard {
  board: WcBoard | null;
  loading: boolean;
  error: string | null;
}

export function useWcBoard(): UseWcBoard {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [board, setBoard] = useState<WcBoard | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // RPC was added in migration 20260526040500_wc2026_board_rpc; cast keeps
     // us moving until the generated Database types are regenerated.
    (supabase.rpc as unknown as (name: string) => Promise<{ data: unknown; error: { message: string } | null }>)("get_wc2026_board")
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) {
          setError(rpcError.message);
          setBoard(null);
        } else {
          setBoard((data ?? null) as WcBoard | null);
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supabase]);

  return { board, loading, error };
}
